import {
  Bell,
  CircleHelp,
  Moon,
  Search,
  Sun,
} from 'lucide-react';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { NotificationCenter } from '../features/notifications/notification-center';
import { useNotifications } from '../features/notifications/use-notifications';
import { useTheme } from '../providers/ThemeProvider';
import { Avatar } from '../components/ui/avatar';
import {
  DropdownRoot,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownDestructiveItem,
  DropdownSeparator,
} from '../components/ui/dropdown';
import { TooltipProvider } from '../components/ui/tooltip';
import { useAuthStore } from '../stores/auth-store';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

// ── Breadcrumb logic ──────────────────────────────────────────

const segmentLabels: Record<string, string> = {
  dashboard:       'Dashboard',
  contacts:        'Contacts',
  campaigns:       'Campaigns',
  automations:     'Automations',
  segments:        'Segments',
  products:        'Products',
  templates:       'Templates',
  compose:         'Compose',
  email:           'Email editor',
  whatsapp:        'WhatsApp editor',
  push:            'Push editor',
  popups:          'Popups',
  connectors:      'Connectors',
  analytics:       'Analytics',
  settings:        'Settings',
  general:         'General',
  team:            'Team',
  'sender-domains':'Sender Domains',
  'api-keys':      'API Keys',
  webhooks:        'Webhooks',
  billing:         'Billing',
  new:             'New',
};

function isId(seg: string) {
  return /^[0-9a-f-]{8,}$/i.test(seg) || /^\d+$/.test(seg);
}

function useBreadcrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return [{ label: 'Dashboard', to: '/dashboard', isLast: true }];

  const crumbs = segments.map((seg, i) => {
    const to = '/' + segments.slice(0, i + 1).join('/');
    const label = isId(seg) ? 'Detail' : (segmentLabels[seg] ?? (seg.charAt(0).toUpperCase() + seg.slice(1)));
    return { label, to, isLast: i === segments.length - 1 };
  });

  return crumbs;
}

// ── Topbar ────────────────────────────────────────────────────

interface TopbarProps {
  onOpenCommandPalette: () => void;
  onOpenShortcuts: () => void;
}

export function Topbar({ onOpenCommandPalette, onOpenShortcuts }: TopbarProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { resolvedTheme, setTheme } = useTheme();
  const breadcrumbs = useBreadcrumbs();
  const isDark = resolvedTheme === 'dark';

  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const notificationRef = React.useRef<HTMLDivElement | null>(null);
  const notificationState = useNotifications(notificationsOpen, () => setNotificationsOpen(true));
  const unreadCount = notificationState.unreadCount;
  const badge = unreadCount > 99 ? '99+' : String(unreadCount);
  const notifLabel = unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications';
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User';

  React.useEffect(() => {
    function onPointerDown(e: MouseEvent) {
      if (!notificationRef.current?.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  function handleSignOut() {
    clearAuth();
    navigate('/login');
  }

  return (
    <TooltipProvider>
      <header
        className={cn(
          'flex h-[var(--topbar-height)] shrink-0 items-center gap-3 pl-4 pr-4 z-20',
          'border-b border-border-subtle bg-surface-card/90 backdrop-blur-md sticky top-0',
        )}
      >
        {/* LEFT — Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center text-sm">
          <ol className="flex items-center gap-0">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.to}>
                {index > 0 && (
                  <li
                    aria-hidden="true"
                    className="mx-1.5 select-none text-text-disabled font-normal text-xs"
                  >
                    /
                  </li>
                )}
                <li>
                  {crumb.isLast ? (
                    <span
                      className="font-semibold text-text-primary truncate tracking-tight"
                      aria-current="page"
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      to={crumb.to}
                      className="text-text-tertiary hover:text-text-secondary transition-colors truncate rounded focus-visible:outline-2 focus-visible:outline-brand-default focus-visible:outline-offset-2"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </li>
              </React.Fragment>
            ))}
          </ol>
        </nav>

        {/* RIGHT — Search pill + action icons */}
        <div className="flex items-center gap-1 shrink-0">
          {/* Pill search trigger */}
          <button
            type="button"
            onClick={onOpenCommandPalette}
            aria-label="Open command palette (⌘K)"
            aria-keyshortcuts="Meta+k Control+k"
            className={cn(
              'hidden md:flex items-center gap-2 h-8 w-[300px] shrink-0 rounded-full px-3',
              'border border-border-subtle bg-bg-subtle hover:bg-bg-muted transition-colors',
              'text-sm text-text-tertiary',
              'focus-visible:outline-2 focus-visible:outline-brand-default focus-visible:outline-offset-2',
            )}
          >
            <Search className="h-3.5 w-3.5 shrink-0 text-text-disabled" aria-hidden="true" />
            <span className="flex-1 text-left text-[13px]">Search contacts, campaigns…</span>
            <kbd
              className="hidden lg:inline-flex h-5 items-center gap-0.5 rounded-md border border-border-subtle bg-surface-card px-1.5 font-mono text-[10px] text-text-tertiary"
              aria-hidden="true"
            >
              ⌘K
            </kbd>
          </button>

          {/* Notifications */}
          <div ref={notificationRef} className="relative">
            <button
              type="button"
              onClick={() => setNotificationsOpen((o) => !o)}
              aria-label={notifLabel}
              aria-expanded={notificationsOpen}
              aria-haspopup="dialog"
              className={cn(
                'relative flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                'text-text-tertiary hover:text-text-primary hover:bg-bg-subtle',
                'focus-visible:outline-2 focus-visible:outline-brand-default focus-visible:outline-offset-2',
              )}
            >
              <Bell className="h-[18px] w-[18px]" aria-hidden="true" />
              {unreadCount > 0 && (
                <span
                  className="absolute right-0.5 top-0.5 flex min-w-[16px] items-center justify-center rounded-full bg-danger-default px-1 text-[9px] font-bold leading-[16px] text-white"
                  aria-hidden="true"
                >
                  {badge}
                </span>
              )}
            </button>
            {notificationsOpen && (
              <NotificationCenter
                notifications={notificationState.notifications}
                unreadCount={notificationState.unreadCount}
                hasMore={notificationState.hasMore}
                filter={notificationState.filter}
                onFilterChange={notificationState.setFilter}
                onLoadMore={notificationState.loadMore}
                onMarkRead={notificationState.markRead}
                onMarkAllRead={notificationState.markAllRead}
                onClose={() => setNotificationsOpen(false)}
              />
            )}
          </div>

          {/* Help / shortcuts */}
          <button
            type="button"
            onClick={onOpenShortcuts}
            aria-label="Keyboard shortcuts and help"
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
              'text-text-tertiary hover:text-text-primary hover:bg-bg-subtle',
              'focus-visible:outline-2 focus-visible:outline-brand-default focus-visible:outline-offset-2',
            )}
          >
            <CircleHelp className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>

          {/* Divider */}
          <div className="mx-1 h-5 w-px bg-border-subtle" aria-hidden="true" />

          {/* User avatar → dropdown */}
          <DropdownRoot>
            <DropdownTrigger asChild>
              <button
                type="button"
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-default focus-visible:ring-offset-1"
                aria-label="User menu"
              >
                <Avatar name={displayName} size="sm" />
              </button>
            </DropdownTrigger>
            <DropdownContent align="end" sideOffset={8}>
              <div className="px-3 py-2">
                <p className="text-sm font-semibold text-text-primary">{displayName}</p>
                <p className="text-xs text-text-tertiary">{user?.email}</p>
              </div>
              <DropdownSeparator />
              <DropdownItem onClick={onOpenShortcuts}>
                <CircleHelp className="h-4 w-4" />
                Keyboard shortcuts
              </DropdownItem>
              <DropdownItem onClick={() => setTheme(isDark ? 'light' : 'dark')}>
                {isDark ? (
                  <><Sun className="h-4 w-4" /> Light mode</>
                ) : (
                  <><Moon className="h-4 w-4" /> Dark mode</>
                )}
              </DropdownItem>
              <DropdownSeparator />
              <DropdownDestructiveItem onClick={handleSignOut}>
                Sign out
              </DropdownDestructiveItem>
            </DropdownContent>
          </DropdownRoot>
        </div>
      </header>
    </TooltipProvider>
  );
}
